import nsjGlobals from '../../core/globals/nsj-globals';

export interface ICampoOcultavel {
    campo: string;
    label: string;
}

export interface IGrupoCamposOcultaveis {
    grupo: string;
    campos: ICampoOcultavel[];
}

/** Campo com o grupo a que pertence, para as listas de seleção não agrupadas */
export interface ICampoOcultavelPlano extends ICampoOcultavel {
    grupo: string;
}

export interface IAbaColaborador {
    slug: string;
    index: number;
    nome: string;
}

/**
 * Apoia as configurações de campos e abas ocultos para usuários em modo gestor
 * (MRH_CAMPOS_OCULTOS_GESTOR e MRH_ABAS_OCULTAS_GESTOR).
 *
 * O mascaramento dos campos é feito pelo backend, que devolve o valor como
 * '********'; aqui ficam apenas os catálogos usados na tela de configuração e
 * a ocultação das abas do cadastro do colaborador, que é responsabilidade do
 * frontend.
 *
 * Registrado no app.module.ts, e não com angular.module('main') aqui: a classe
 * é usada como valor pelo ConfiguracoesIndexController, então este arquivo é
 * empacotado no chunk vendor e avaliado antes de o módulo 'main' existir.
 */
export class OcultacaoGestorService {

    static $inject = ['PermissaoService'];

    /**
     * Valor devolvido pelo backend no lugar do conteúdo dos campos ocultos.
     * O filtro `mascaraGestor` (app/shared/filters/filters.js) espelha esse valor.
     */
    static readonly MASCARA = '********';

    static readonly CHAVE_CAMPOS_OCULTOS = 'MRH_CAMPOS_OCULTOS_GESTOR';
    static readonly CHAVE_ABAS_OCULTAS = 'MRH_ABAS_OCULTAS_GESTOR';

    /**
     * Permissão cujo perfil define se o usuário está em modo gestor na tela de
     * colaboradores. É a mesma que o backend usa para decidir o mascaramento.
     */
    static readonly PERMISSAO_MODO_GESTOR = 'visualizar_funcionarios';

    /** Identificação básica do colaborador: o gestor sempre precisa ver (blocklist do backend) */
    static readonly CAMPOS_NAO_OCULTAVEIS = ['trabalhador', 'codigo', 'nome'];

    /** Abas com ações do gestor: não podem ser ocultadas (blocklist do backend) */
    static readonly ABAS_NAO_OCULTAVEIS = ['arquivos', 'beneficios'];

    /**
     * Abas do cadastro do colaborador (Trabalhadores/show.html). O índice é o
     * atributo index da uib-tab; o slug é o valor gravado na configuração.
     */
    static readonly ABAS: IAbaColaborador[] = [
        { slug: 'dados-gerais', index: 1, nome: 'Dados Gerais' },
        { slug: 'contatos', index: 2, nome: 'Contatos' },
        { slug: 'dependentes', index: 3, nome: 'Dependentes' },
        { slug: 'historicos', index: 4, nome: 'Históricos' },
        { slug: 'ferias', index: 5, nome: 'Férias' },
        { slug: 'afastamentos', index: 6, nome: 'Afastamentos' },
        { slug: 'documentos', index: 7, nome: 'Documentos' },
        { slug: 'vale-transporte', index: 8, nome: 'Vale-transporte' },
        { slug: 'arquivos', index: 9, nome: 'Arquivos' },
        { slug: 'beneficios', index: 10, nome: 'Benefícios' },
    ];

    /**
     * Campos do colaborador oferecidos na tela de configuração. São nomes de
     * campos do payload da API, agrupados com os MESMOS títulos das seções da
     * ficha cadastral (Dados Gerais, Dados Pessoais, Documentação, Outras
     * Informações), para o administrador reconhecer a divisão que já conhece
     * do relatório.
     *
     * Ficam fora do catálogo, de propósito:
     * - campos booleanos (deficiente*, primeiroemprego, jornadareduzida...):
     *   o backend mascara `false` como '********', que é truthy e inverteria
     *   os ng-if que dependem deles;
     * - `tipo`, `status` e `situacao`: governam ng-if de abas e de trechos da
     *   tela (inclusive a exibição das próprias abas de Férias e Vale-transporte).
     */
    static readonly CAMPOS: IGrupoCamposOcultaveis[] = [
        {
            /**
             * Grupos e alocação espelham as seções da ficha cadastral do
             * colaborador (pdfs/ficha-cadastral.html.twig), para o administrador
             * reconhecer os campos pela mesma divisão que vê no relatório.
             * Campos que a ficha não exibe ficam na seção mais afim.
             */
            grupo: 'Dados Gerais',
            campos: [
                { campo: 'cargo', label: 'Cargo' },
                { campo: 'nivelcargo', label: 'Nível do cargo' },
                { campo: 'departamento', label: 'Departamento' },
                { campo: 'dataadmissao', label: 'Data de admissão' },
                { campo: 'datarescisao', label: 'Data de rescisão' },
                { campo: 'motivorescisao', label: 'Motivo da rescisão' },
                { campo: 'datafimcontrato', label: 'Fim do contrato' },
                { campo: 'dataprorrogacaocontrato', label: 'Prorrogação do contrato' },
                { campo: 'salariofixo', label: 'Salário contratual' },
                { campo: 'unidadesalariofixo', label: 'Unidade do salário' },
                { campo: 'salariovariavel', label: 'Salário variável' },
                { campo: 'descricaosalariovariavel', label: 'Descrição do salário variável' },
                { campo: 'outrosrendimentos', label: 'Total de outros rendimentos' },
                { campo: 'rendimentos', label: 'Detalhamento de outros rendimentos' },
                { campo: 'horas', label: 'Horas mensais' },
                { campo: 'horassemanais', label: 'Horas semanais' },
                { campo: 'categoriatrabalhador', label: 'Categoria do trabalhador' },
                { campo: 'codigocategoriafgts', label: 'Categoria do FGTS' },
                { campo: 'datafgts', label: 'Data do FGTS' },
            ],
        },
        {
            grupo: 'Dados Pessoais',
            campos: [
                { campo: 'datanascimento', label: 'Data de nascimento' },
                { campo: 'sexo', label: 'Sexo' },
                { campo: 'estadocivil', label: 'Estado civil' },
                { campo: 'nomepai', label: 'Nome do pai' },
                { campo: 'nomemae', label: 'Nome da mãe' },
                { campo: 'cidadenascimento', label: 'Cidade de nascimento' },
                { campo: 'ufnascimento', label: 'UF de nascimento' },
                { campo: 'nomemunicipionascimento', label: 'Município de nascimento' },
                { campo: 'nomepaisnascimento', label: 'País de nascimento' },
                // ocultar 'sangue' também oculta o derivado sangueString (backend)
                { campo: 'sangue', label: 'Tipo sanguíneo' },
                { campo: 'raca', label: 'Raça/cor' },
                { campo: 'grauinstrucao', label: 'Grau de instrução' },
                { campo: 'tipologradouro', label: 'Tipo de logradouro' },
                { campo: 'logradouro', label: 'Logradouro' },
                { campo: 'numero', label: 'Número do endereço' },
                { campo: 'complemento', label: 'Complemento' },
                { campo: 'bairro', label: 'Bairro' },
                { campo: 'cidade', label: 'Cidade' },
                { campo: 'uf', label: 'Estado' },
                { campo: 'cep', label: 'CEP' },
                { campo: 'dddtel', label: 'DDD do telefone' },
                { campo: 'telefone', label: 'Telefone' },
                { campo: 'dddcel', label: 'DDD do celular' },
                { campo: 'celular', label: 'Celular' },
                { campo: 'email', label: 'E-mail' },
                { campo: 'nomeemergencia', label: 'Contato de emergência - nome' },
                { campo: 'dddtelemergencia', label: 'Contato de emergência - DDD' },
                { campo: 'telefoneemergencia', label: 'Contato de emergência - telefone' },
                { campo: 'relacaoemergencia', label: 'Contato de emergência - relação' },
                { campo: 'identificacaonasajon', label: 'E-mail corporativo' },
                { campo: 'identidadecorporativa', label: 'Identidade corporativa' },
                { campo: 'dddtelcorporativo', label: 'DDD do telefone corporativo' },
                { campo: 'telefonecorporativo', label: 'Telefone corporativo' },
                { campo: 'ramalcorporativo', label: 'Ramal corporativo' },
                { campo: 'banco', label: 'Banco' },
                { campo: 'agencia', label: 'Agência' },
                { campo: 'tiporecebimentosalario', label: 'Tipo de recebimento' },
                { campo: 'numerocontasalario', label: 'Conta salário' },
                { campo: 'numerocontasalariodv', label: 'DV da conta salário' },
                { campo: 'numerocartaovt', label: 'Número do cartão de VT' },
            ],
        },
        {
            grupo: 'Documentação',
            campos: [
                { campo: 'cpf', label: 'CPF' },
                { campo: 'nis', label: 'PIS/NIS' },
                { campo: 'numerorg', label: 'Identidade (RG)' },
                { campo: 'orgaoemissorrg', label: 'Órgão emissor do RG' },
                { campo: 'ufrg', label: 'UF do RG' },
                { campo: 'dataexpedicaorg', label: 'Expedição do RG' },
                { campo: 'numeroctps', label: 'Número da CTPS' },
                { campo: 'seriectps', label: 'Série da CTPS' },
                { campo: 'ufctps', label: 'UF da CTPS' },
                { campo: 'dataexpedicaoctps', label: 'Expedição da CTPS' },
                { campo: 'numerote', label: 'Título de eleitor' },
                { campo: 'ufte', label: 'UF do título de eleitor' },
                { campo: 'numerocnh', label: 'Número da CNH' },
                { campo: 'categoriacnh', label: 'Categoria da CNH' },
                { campo: 'orgaoemissorcnh', label: 'Órgão emissor da CNH' },
                { campo: 'ufcnh', label: 'UF da CNH' },
                { campo: 'datavalidadecnh', label: 'Validade da CNH' },
                { campo: 'dataexpedicaocnh', label: 'Expedição da CNH' },
                { campo: 'numerocertidao', label: 'Número da certidão' },
                { campo: 'numerocr', label: 'Certificado de reservista' },
            ],
        },
        {
            /**
             * Seção própria da ficha cadastral: oculta os reajustes salariais
             * exibidos na ficha (datas, salários anterior/novo e descrição).
             */
            grupo: 'Reajustes Salariais',
            campos: [
                { campo: 'reajustes', label: 'Reajustes salariais' },
            ],
        },
        {
            grupo: 'Outras Informações',
            campos: [
                { campo: 'inicioperiodoaquisitivoferias', label: 'Início do período aquisitivo de férias' },
                { campo: 'datavencimentoatestadomedico', label: 'Vencimento do atestado médico' },
                { campo: 'dependentes', label: 'Dependentes' },
                { campo: 'observacao', label: 'Observação' },
            ],
        },
        {
            /**
             * Campos exclusivos do payload de colaboradores locados, que o
             * backend também mascara (LocadosService). Não há seção
             * correspondente na ficha cadastral.
             */
            grupo: 'Colaboradores locados',
            campos: [
                { campo: 'salario', label: 'Salário do locado' },
                { campo: 'prestadorservico', label: 'Prestador de serviço' },
                { campo: 'datavencimento', label: 'Vencimento do atestado do locado' },
                { campo: 'tipologradouroresidencia', label: 'Tipo de logradouro da residência' },
                { campo: 'logradouroresidencia', label: 'Logradouro de residência' },
                { campo: 'numerologradouroresidencia', label: 'Número da residência' },
                { campo: 'complementologradouroresidencia', label: 'Complemento da residência' },
                { campo: 'bairroresidencia', label: 'Bairro de residência' },
                { campo: 'cidaderesidencia', label: 'Cidade de residência' },
                { campo: 'ufresidencia', label: 'Estado de residência' },
                { campo: 'cepresidencia', label: 'CEP de residência' },
            ],
        },
    ];

    /**
     * Caches por sessão: modo gestor e abas ocultas não mudam sem recarregar a
     * página (o /globals é lido no login, mesma característica das flags
     * LIBERAR_*), e podeVerAba() é avaliado por dez ng-if a cada digest.
     */
    private cacheModoGestor?: boolean;
    private cacheAbasOcultas?: string[];

    constructor(private PermissaoService: any) { }

    /**
     * Todas as abas do cadastro do colaborador, na ordem em que aparecem.
     */
    todasAsAbas(): IAbaColaborador[] {
        return OcultacaoGestorService.ABAS;
    }

    /**
     * Campos oferecidos na tela de configuração, sem os itens da blocklist.
     */
    camposConfiguraveis(): IGrupoCamposOcultaveis[] {
        return OcultacaoGestorService.CAMPOS.map((grupo) => ({
            grupo: grupo.grupo,
            campos: grupo.campos.filter(
                (campo) => !OcultacaoGestorService.CAMPOS_NAO_OCULTAVEIS.includes(campo.campo)
            ),
        })).filter((grupo) => grupo.campos.length > 0);
    }

    /**
     * Mesmo catálogo de campos, achatado na ordem dos grupos: as listas de
     * seleção não são agrupadas, mas campos do mesmo grupo continuam vizinhos.
     */
    camposConfiguraveisPlanos(): ICampoOcultavelPlano[] {
        return this.camposConfiguraveis().reduce(
            (planos: ICampoOcultavelPlano[], grupo) => planos.concat(
                grupo.campos.map((campo) => ({ ...campo, grupo: grupo.grupo }))
            ),
            []
        );
    }

    /**
     * Abas oferecidas na tela de configuração, sem os itens da blocklist.
     */
    abasConfiguraveis(): IAbaColaborador[] {
        return OcultacaoGestorService.ABAS.filter(
            (aba) => !OcultacaoGestorService.ABAS_NAO_OCULTAVEIS.includes(aba.slug)
        );
    }

    /**
     * Indica se o usuário logado acessa a tela de colaboradores em modo gestor.
     * Usuários com acesso completo e administradores não são impactados.
     */
    modoGestor(): boolean {
        if (this.cacheModoGestor === undefined) {
            this.cacheModoGestor = this.PermissaoService.modoGestorPermissao(OcultacaoGestorService.PERMISSAO_MODO_GESTOR);
        }
        return this.cacheModoGestor;
    }

    abasOcultas(): string[] {
        if (this.cacheAbasOcultas === undefined) {
            this.cacheAbasOcultas = this.listaConfigurada(
                OcultacaoGestorService.CHAVE_ABAS_OCULTAS,
                OcultacaoGestorService.ABAS_NAO_OCULTAVEIS
            );
        }
        return this.cacheAbasOcultas;
    }

    /**
     * Indica se a aba deve ser ocultada por configuração para o usuário logado.
     */
    abaOcultaParaGestor(slug: string): boolean {
        return this.modoGestor() && this.abasOcultas().includes(slug);
    }

    abaPorIndex(index: number): IAbaColaborador | undefined {
        return OcultacaoGestorService.ABAS.find((aba) => aba.index === index);
    }

    /**
     * Lê a configuração do /globals, que sempre chega como array (vazio quando
     * não configurada), e descarta os itens da blocklist.
     */
    private listaConfigurada(chave: string, naoOcultaveis: string[]): string[] {
        const configuracoes = nsjGlobals.get('configuracoes') as Record<string, unknown> | null;
        const valor = configuracoes ? configuracoes[chave] : null;

        if (!Array.isArray(valor)) {
            return [];
        }

        return valor.filter(
            (item): item is string =>
                typeof item === 'string' && item.trim() !== '' && !naoOcultaveis.includes(item)
        );
    }
}
